import { Action, Reducer } from 'redux';
import { AppThunkActionAsync } from '../..';
import { AppServices } from '../../../AppServices';
import { ResultType } from '../../Models/Api/ApiResponse';
import { ToxnotServiceClient } from '../../ToxnotServiceClient';
import {SharedMaterialEngageResponse} from "../../Models/Api/SharedMaterials/SharedMaterialEngageResponse";

// ===============================================================================

//
// State definition
//

export interface EngageConnectFormData {
    location: string,
    productType: string,
    moreInformation: string,
    name: string,
    email: string,
    productName: string,
    projectName: string
}

export interface EngagementState {
    isLoading: boolean;
    bundle: SharedMaterialEngageResponse | null
}

// ===============================================================================

//
// Action definitions
//

const RequestEngageBundle = "REQUEST_ENGAGE_BUNDLE";
interface RequestEngageBundleAction {
    type: typeof RequestEngageBundle;
}

const ReceiveEngageBundle = "RECEIVE_ENGAGE_BUNDLE";
interface ReceiveEngageBundleAction {
    type: typeof ReceiveEngageBundle;
    bundle: SharedMaterialEngageResponse | null;
}

// ===============================================================================

//
// Action Creators
//

type KnownAction =
    RequestEngageBundleAction | ReceiveEngageBundleAction;

export const actionCreators = {
    loadEngageBundleAsync: (productName: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState) {
            return;
        }

        dispatch({ type: RequestEngageBundle });

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.getSharedMaterialEngage("Assa Abloy", productName);

        if (apiResult.result === ResultType.NotFound) {
            dispatch({
                type: ReceiveEngageBundle,
                bundle: null
            });
            return;
        }

        if (!apiResult.success || (apiResult.data === null)) {
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        dispatch({
            type: ReceiveEngageBundle,
            bundle: apiResult.data
        });

        if (appState.appServices) {
            appState.appServices.trackCurrentUrlPath();
            appState.appServices.trackEvent("view-engagement-page", { sharedMaterialId: apiResult.data.coreData.sharedMaterialId, materialName: apiResult.data.coreData.materialName, ownerId: apiResult.data.coreData.ownerId });
        }
    },
    sendConnectWithForm: (appServices: AppServices): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();
        if (!appState || !appState.engageState || !appState.engageState.bundle) {
            return;
        }

        const location = (document.getElementById("connect-location") as HTMLInputElement).value.trim();
        const productType = (document.getElementById("connect-product-type") as HTMLInputElement).value.trim();
        const name = (document.getElementById("connect-name") as HTMLInputElement).value.trim();
        const email = (document.getElementById("connect-email") as HTMLInputElement).value.trim();

        if (location === "" || productType === "" || name === "" || email === "") {
            $("#connect-form-empty-fields").show();
            return;
        }

        const serviceClient = new ToxnotServiceClient();

        const data: EngageConnectFormData = {
            location: location,
            productType: productType,
            moreInformation: (document.getElementById("connect-more-information") as HTMLInputElement).value.trim(),
            name: name,
            email: email,
            productName: appState.engageState.bundle.coreData.materialName,
            projectName: (document.getElementById("connect-project-name") as HTMLInputElement).value.trim()
        }

        const result = await serviceClient.engageConnectWithAssaAbloy(data);

        if (result.result === "BadEmailAddress") {
            $("#connect-form-invalid-email").show();
        }
        else if (result.success) {
            $("#connect-form-success").show();
            $("#send-email-button").hide();
            $("#connect-form-body").hide();
            $("#connect-form-invalid-email").hide();
            $("#connect-form-empty-fields").hide();
            $("#close-connect-form-button").show();
            $("#cancel-connect-form-button").hide();
            appServices.trackEvent("engagement-send-connect-form", { materialId: appState.engageState.bundle.coreData.sharedMaterialId, productName: appState.engageState.bundle.coreData.materialName, ownerId: appState.engageState.bundle.coreData.ownerId })
        }
        else {
            $("#connect-form-error").show();
            $("#connect-form-body").hide();
            $("#send-email-button").hide();
            $("#close-connect-form-button").show();
            $("#cancel-connect-form-button").hide();
        }
    }
};

// ===============================================================================

//
// Reducer
//

const initialState: EngagementState = {
    isLoading: true,
    bundle: null
};

export const reducer: Reducer<EngagementState> = (state: EngagementState | undefined, incomingAction: Action): EngagementState => {
    if (state === undefined) {
        return initialState;
    }

    switch (incomingAction.type) {
        case RequestEngageBundle:
            return {
                isLoading: true,
                bundle: null
            };

        case ReceiveEngageBundle:
            const receiveAction = incomingAction as ReceiveEngageBundleAction;

            return {
                ...state,
                isLoading: false,
                bundle: receiveAction.bundle
            };
    }

    return state;
};
